import { useEffect, useState } from 'react';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';

const testimonials = [
  {
    name: '张先生',
    role: '内容创作者',
    quote: '一区二区三区的分区体系让我快速找到了适合自己的内容。从一区的基础入门开始，现在已经能够流畅地阅读三区的深度文章，学习效率提升了好几倍。',
    rating: 5,
  },
  {
    name: '李女士',
    role: '自由职业者',
    quote: '这套分区导航指南太实用了！以前在海量内容中找不到方向，现在按照分区体系有条理地学习，目标明确，进步明显。强烈推荐给所有内容学习者。',
    rating: 5,
  },
  {
    name: '王同学',
    role: '在校研究生',
    quote: '作为学术研究者，三区的深度内容给了我很大启发。分区体系设计科学合理，让不同阶段的学习者都能找到最适合自己的内容层级。',
    rating: 5,
  },
  {
    name: '赵总',
    role: '企业培训负责人',
    quote: '我们在公司内部培训中引入了这一分区理念，将培训材料按三个层级组织。员工反馈非常好，学习路径清晰，培训效果显著提升。',
    rating: 5,
  },
  {
    name: '陈老师',
    role: '教育工作者',
    quote: '一区二区三区的分层理念与教育学的循序渐进原则高度一致。我将这一方法应用到了课程设计中，学生的学习积极性和掌握程度都有了明显提高。',
    rating: 5,
  },
  {
    name: '刘工',
    role: '高级工程师',
    quote: '在三区找到了许多前沿的技术深度分析，这些内容在其他平台很难找到。感谢这个平台，让我们专业从业者有一个深入交流和学习的高质量空间。',
    rating: 5,
  },
];

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    document.title = '关于我们 | 一区二区三区导航指南';
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    const formData = new FormData(form);

    try {
      const res = await fetch('https://readdy.ai/api/form/d9u08pv8l97n3eas3eug', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = (parsed?.meta as Record<string, string>)?.message
          || (parsed?.message as string)
          || (parsed?.meta as Record<string, string>)?.detail
          || '提交失败，请稍后再试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  const breadcrumbItems = [{ label: '首页', href: '/' }, { label: '关于我们' }];

  const orgSchema = {
    '@type': 'Organization',
    name: '分区导航指南',
    url: 'https://hnatawindow.cn/',
    description: '专注一区二区三区内容导航与知识服务，为用户提供最全面的分区说明与实用指南。',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  const breadcrumbSchemaData = {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hnatawindow.cn/' },
      { '@type': 'ListItem', position: 2, name: '关于我们' },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg type="Organization" data={orgSchema} />
      <SchemaOrg type="BreadcrumbList" data={breadcrumbSchemaData} />
      <Header />

      <main className="pt-16">
        <section className="w-full bg-background-100 py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />
            <div className="mt-8 text-center">
              <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
                关于我们
              </span>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
                品牌介绍
              </h1>
              <p className="text-sm md:text-base text-foreground-500 max-w-3xl mx-auto leading-relaxed">
                分区导航指南致力于为用户提供最全面、最专业的一区二区三区内容导航服务。
              </p>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50 py-16 md:py-24">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
                  我们的使命
                </span>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
                  让每位用户精准定位所需内容
                </h2>
                <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                  <p>
                    <strong>分区导航指南</strong>诞生于一个简单的理念：在海量信息中，每个人都应该能够快速找到最适合自己的内容。
                  </p>
                  <p>
                    我们深入研究了一区二区三区的内容分层理论，结合大量用户反馈和数据验证，构建了这套科学、实用、易用的分区导航体系。无论您是零基础的初学者，还是深耕多年的专业人士，都能在这里找到匹配的内容层级。
                  </p>
                  <p>
                    自成立以来，我们始终坚持以用户需求为核心，持续优化分区标准和内容质量。我们的团队由内容编辑、行业专家和产品设计师组成，共同致力于打造最优质的内容导航体验。
                  </p>
                  <p>
                    我们相信，<strong>一区二区三区</strong>的分区理念不仅是一种内容组织方式，更是一种高效学习的方法论。通过科学的分层，让每位用户都能在自己的节奏中成长和进步。
                  </p>
                </div>
              </div>
              <div className="relative">
                <img
                  src="https://readdy.ai/api/search-image?query=Abstract%20geometric%20composition%20representing%20organized%20knowledge%20and%20content%20structure%2C%20warm%20cream%20and%20terracotta%20tones%2C%20three%20distinct%20layers%20of%20overlapping%20translucent%20shapes%2C%20minimal%20modern%20design%2C%20editorial%20aesthetic%2C%20high%20quality%20abstract%20illustration%20representing%20growth%20and%20learning&width=700&height=560&seq=about-brand-image&orientation=landscape"
                  alt="分区导航指南品牌理念"
                  className="w-full rounded-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-16 md:py-24" id="reputation">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-14">
              <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
                用户口碑
              </span>
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
                听听他们怎么说
              </h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                来自不同行业、不同背景的真实用户反馈，见证分区导航指南的价值。
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.map((t, i) => (
                <div key={i} className="bg-background-50 rounded-2xl border border-background-200 p-6">
                  <div className="flex items-center gap-1 mb-4">
                    {Array.from({ length: t.rating }).map((_, j) => (
                      <i key={j} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-5 italic">
                    &ldquo;{t.quote}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-xs font-semibold text-primary-700">{t.name[0]}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">{t.name}</p>
                      <p className="text-xs text-foreground-400">{t.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50 py-16 md:py-24" id="contact">
          <div className="max-w-2xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium mb-4">
                联系我们
              </span>
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
                有任何问题？随时联系
              </h2>
              <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
                如果您对一区二区三区的分区标准、使用方法有任何疑问，或希望与我们合作，欢迎通过下方表单联系我们。
              </p>
            </div>

            {formStatus === 'success' ? (
              <div className="bg-accent-50 border border-accent-200 rounded-2xl p-8 text-center">
                <div className="w-14 h-14 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-accent-600 text-2xl"></i>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">感谢您的留言！</h3>
                <p className="text-sm text-foreground-500">我们已收到您的消息，会在 2 个工作日内回复。</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-readdy-form="" className="bg-background-50 rounded-2xl border border-background-200 p-6 md:p-8">
                {formStatus === 'error' && (
                  <div className="mb-6 p-4 rounded-xl bg-primary-50 border border-primary-200 text-sm text-primary-700">
                    {formError}
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div className="mb-5">
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    required
                    className="w-full px-4 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                    placeholder="请输入留言主题"
                  />
                </div>
                <div className="mb-6">
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                    placeholder="请输入您的留言内容（最多500字）"
                  ></textarea>
                </div>
                <div className="honeypot-field">
                  <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                </div>
                <button
                  type="submit"
                  className="w-full sm:w-auto px-8 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  提交留言
                  <i className="ri-send-plane-line ml-2"></i>
                </button>
              </form>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}